import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { 
  MessageCircle, 
  CheckSquare, 
  Activity, 
  Moon, 
  Heart, 
  Sparkles,
  Wind,
  Apple,
  Calendar,
  Focus,
  ArrowRight
} from 'lucide-react';

interface Action {
  id: string;
  label: string;
  description: string;
  actionType: string;
  route: string;
  priority: 'primary' | 'secondary';
}

interface NextActionsGridProps {
  actions: Action[];
}

export const NextActionsGrid: React.FC<NextActionsGridProps> = ({ actions }) => {
  const navigate = useNavigate();

  const getActionIcon = (actionType: string) => {
    const iconMap: Record<string, any> = {
      breathwork: Wind,
      protocol: Activity,
      checkin: CheckSquare,
      talk_to_therappi: MessageCircle,
      future_self: Sparkles,
      sanctuary: Heart,
      sleep: Moon,
      nutrition: Apple,
      cycle: Calendar,
      meditation: Focus
    };
    return iconMap[actionType] || CheckSquare;
  };

  if (actions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-accent" />
        </div>
        <p className="text-lg text-foreground/80 font-light mb-2">You're all caught up</p>
        <p className="text-sm text-muted-foreground font-light">
          Take a moment to breathe, or explore your tools below
        </p>
      </div>
    );
  }

  const primaryAction = actions.find(a => a.priority === 'primary');
  const secondaryActions = actions.filter(a => a.priority === 'secondary');

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-light text-foreground tracking-wide">Your Next Best Steps</h2>
      
      {/* Primary Action - Featured Card */}
      {primaryAction && (
        <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-primary/15 via-accent/10 to-secondary/5 shadow-lg hover:shadow-xl transition-all duration-500">
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-radial from-accent/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                {React.createElement(getActionIcon(primaryAction.actionType), {
                  className: "w-7 h-7 text-primary"
                })}
              </div>
              <div className="flex-1 space-y-3">
                <h3 className="text-xl font-light text-foreground tracking-wide">
                  {primaryAction.label}
                </h3>
                <p className="text-sm text-muted-foreground font-light leading-relaxed">
                  {primaryAction.description}
                </p>
                <Button 
                  onClick={() => navigate(primaryAction.route)}
                  className="mt-4 group"
                  size="lg"
                >
                  <span className="font-light tracking-wide">Begin</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Secondary Actions - Compact Grid */}
      {secondaryActions.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4">
          {secondaryActions.map((action) => {
            const Icon = getActionIcon(action.actionType);
            return (
              <Card 
                key={action.id} 
                className="group border-0 bg-card/50 backdrop-blur-sm hover:bg-card/80 hover:shadow-md transition-all duration-300 cursor-pointer"
                onClick={() => navigate(action.route)}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-light text-foreground tracking-wide text-sm">
                        {action.label}
                      </h4>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};